import React from 'react';
import styles from './ConfigPanel.module.scss';
import { ItemSchema } from '../../assets';
import { useArrayActions } from '../../hooks/useArrayActions';
import { Draggable } from 'react-beautiful-dnd';
import { Card, Avatar, Button } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { ColorPicker } from '../ColorPicker';

export interface Layer {
  item: string;
  colors: string[];
}

type Props = {
  colors: string[];
  schema: ItemSchema;
  draggableIndex?: number;
  remove?: () => any;
} & (
  | {
      changeColors?: undefined;
      changeLayer: (updater: (old: Layer) => Layer) => any;
    }
  | {
      changeLayer?: undefined;
      changeColors: (update: (old: string[]) => string[]) => any;
    }
);

export function BoxLayer(props: Props) {
  const {
    colors: colorArr,
    schema,
    changeColors: maybeChangeColors,
    changeLayer,
    remove,
    draggableIndex,
  } = props;

  const changeColors = React.useCallback(
    (f: (old: string[]) => string[]) => {
      if (changeLayer) {
        changeLayer(old => ({ ...old, colors: f(old.colors) }));
      } else {
        maybeChangeColors!(old => f(old));
      }
    },
    [changeLayer, maybeChangeColors]
  );

  const [colors] = useArrayActions(colorArr, changeColors);

  const tile = (
    <Card
      hoverable={draggableIndex !== undefined}
      size='small'
      style={{ cursor: 'inherit' }}
      actions={colors.map(({ item, replace }, i) => {
        return (
          <ColorPicker
            key={i}
            color={item}
            title={schema.colors[i].name}
            setColor={replace}
            suggestions={schema.colors[i].suggestions}
          />
        );
      })}>
      <div className={styles.layer}>
        {schema.iconUrl ? (
          <Avatar shape='square' size='large' src={schema.iconUrl} />
        ) : null}
        <h2>{schema.title}</h2>
        {remove ? (
          <Button
            icon={<DeleteOutlined />}
            danger
            shape='circle'
            onClick={remove}
          />
        ) : null}
      </div>
    </Card>
  );

  if (draggableIndex === undefined) return tile;
  return (
    <Draggable draggableId={schema.id} index={draggableIndex}>
      {provided => (
        <div
          className={styles.layerContainer}
          ref={provided.innerRef}
          {...provided.dragHandleProps}
          {...provided.draggableProps}>
          {tile}
        </div>
      )}
    </Draggable>
  );
}
